import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { createCheckout, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Eyebrow, Reveal } from "@/components/Reveal";

export const Carrello = () => {
  const { items, updateQuantity, removeItem, total } = useCart();
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("annullato") === "1") {
      toast.info("Pagamento annullato", { description: "Il carrello è intatto, puoi riprovare quando vuoi." });
    }
  }, [searchParams]);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout(items);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("Pagamento non riuscito", {
        description: "Riprova tra qualche istante.",
      });
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 lg:pt-36 pb-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Eyebrow>Il tuo ordine</Eyebrow>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-white">
            Carrello
          </h1>
        </Reveal>

        {items.length === 0 ? (
          <div data-testid="cart-empty-state" className="mt-20 flex flex-col items-center rounded-3xl border border-lineblue bg-panel py-24 text-center">
            <ShoppingBag className="h-12 w-12 text-cobalt" />
            <p className="mt-6 font-display text-2xl font-semibold text-white">
              Il carrello è vuoto
            </p>
            <p className="mt-3 max-w-sm text-sm text-slate-400">
              Esplora il catalogo e scegli il pacchetto adatto al tuo prossimo progetto video.
            </p>
            <Link
              to="/pacchetti"
              data-testid="cart-empty-cta"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-electric px-7 py-3.5 text-sm font-bold text-ink transition-all duration-300 hover:bg-glow"
            >
              Esplora i pacchetti
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid gap-10 lg:grid-cols-[1.6fr_1fr]">
            <div data-testid="cart-items-list" className="space-y-4">
              {items.map((item) => (
                <div
                  key={item.slug}
                  data-testid={`cart-item-${item.slug}`}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-5 rounded-2xl border border-lineblue bg-panel p-5 transition-colors duration-300 hover:border-glow/50"
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-24 w-full rounded-xl object-cover sm:w-36"
                  />
                  <div className="flex-1">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-glow">{item.tag}</p>
                    <p className="mt-1 font-display text-lg font-semibold leading-snug text-white">
                      {item.title}
                    </p>
                    <p className="mt-1 text-sm text-slate-400">{formatPrice(item.price)} / progetto</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center rounded-full border border-lineblue">
                      <button
                        data-testid={`cart-dec-${item.slug}`}
                        onClick={() => updateQuantity(item.slug, -1)}
                        className="flex h-10 w-10 items-center justify-center rounded-l-full text-slate-300 hover:text-electric"
                        aria-label={`Diminuisci ${item.title}`}
                      >
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <span data-testid={`cart-qty-${item.slug}`} className="w-8 text-center font-display font-bold text-white">
                        {item.quantity}
                      </span>
                      <button
                        data-testid={`cart-inc-${item.slug}`}
                        onClick={() => updateQuantity(item.slug, 1)}
                        className="flex h-10 w-10 items-center justify-center rounded-r-full text-slate-300 hover:text-electric"
                        aria-label={`Aumenta ${item.title}`}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="w-24 text-right font-display font-bold text-white">
                      {formatPrice(item.price * item.quantity)}
                    </p>
                    <button
                      data-testid={`cart-remove-${item.slug}`}
                      onClick={() => removeItem(item.slug)}
                      className="text-slate-500 transition-colors hover:text-red-400"
                      aria-label={`Rimuovi ${item.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <aside data-testid="cart-drawer" className="h-fit rounded-3xl border border-lineblue bg-panel p-8 lg:sticky lg:top-28">
              <h2 className="font-display text-xl font-semibold text-white">Riepilogo</h2>
              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Subtotale</span>
                  <span data-testid="cart-subtotal">{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>IVA</span>
                  <span>calcolata allo checkout</span>
                </div>
              </div>
              <div className="mt-6 flex items-end justify-between border-t border-lineblue pt-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Totale</span>
                <span data-testid="cart-total" className="font-display text-3xl font-bold text-white">
                  {formatPrice(total)}
                </span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={handleCheckout}
                disabled={loading}
                className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-electric px-6 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-glow hover:shadow-[0_0_36px_rgba(56,189,248,0.35)] disabled:opacity-60"
              >
                {loading ? "Reindirizzamento…" : "Vai al pagamento sicuro"}
                {!loading && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
              <p className="mt-4 text-center text-[11px] text-slate-500">
                Checkout ospitato e protetto da Stripe
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Carrello;
